"use client";

import { cn } from "@layer/utils/cn";
import {
	type ColumnDef,
	type ColumnFiltersState,
	type FilterFn,
	flexRender,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type PaginationState,
	type RowData,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import {
	Empty,
	ScrollArea,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components";
//import { Empty } from "../empty";
import { LoadingSkeleton } from "../loading-skeleton";
import { Pagination } from "./pagination";
import { Toolbar } from "./toolbar";

type DataTableProps<TData, TValue> = {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	hasToolbar?: boolean;
	isLoading?: boolean;
	hideRowsSelected?: boolean;
	height?: string;
	inputPlaceholder?: string;
	filterField?: string;
	rowStyling?: string;
	pageSize?: number;
	pageIndex?: number;
	onPaginationChange?: OnChangeFn<PaginationState>;
	hasNextPage?: boolean;
	showPagination?: boolean;
	toolbarColor?: string;
	inputColor?: string;
	headerColor?: string;
	paginationColor?: string;
	customSearch?: (term: string) => void;
	searchTerm?: string;
	expandable?: boolean;
	isRunbook?: boolean;
	getRowClassName?: (row: TData) => string;
	onSelectionChange?: (selectedRows: TData[]) => void;
};

// TODO: Clean up where/how we're extending the Meta interface'
declare module "@tanstack/react-table" {
	interface ColumnMeta<TData extends RowData, TValue> {
		id: any;
		label: any;
		values: any;
	}
	interface FilterFns {
		globalContains: FilterFn<unknown>;
	}
}

const globalContainsFilter: FilterFn<any> = (row, _columnId, filterValue) => {
	const search = String(filterValue ?? "")
		.trim()
		.toLowerCase();
	if (!search) return true;

	const cellValues = row
		.getAllCells()
		.map((cell) => cell.getValue())
		.filter((value) => value !== null && value !== undefined);

	const normalisedValues = cellValues.map((value) =>
		typeof value === "number"
			? value.toFixed(2)
			: value instanceof Date
				? value.toISOString()
				: String(value),
	);

	return normalisedValues.some((value) =>
		value.toLowerCase().includes(search),
	);
};

/**
 * @type DataTableProps
 * @name DataTable
 * @todo
 * - Add an abstraction whereby columns provided help generate varios filters, sorts and searches
 * - Allow for table style to be passed in via props - i.e. whether the table is in line or full-screen
 * - See [bar](./pagination.tsx)
 * @remarks
 * The core data table component, imports {@linkcode Pagination} and {@linkcode DataTableToolbar} to create a table with the given data and columns.
 * @param columns - The columns to display in the table
 * @param data - The data corresponding to the columns to display in the table
 * @returns A table component with pagination and toolbar
 */
const DataTable = <TData, TValue>({
	columns,
	data,
	isLoading,
	hasToolbar = true,
	hideRowsSelected = false,
	showPagination = true,
	height,
	inputPlaceholder,
	filterField,
	pageSize: controlledPageSize,
	pageIndex: controlledPageIndex,
	onPaginationChange,
	hasNextPage,
	toolbarColor = "bg-accent-surface-subtle",
	inputColor,
	headerColor,
	paginationColor,
	customSearch,
	searchTerm,
	expandable,
	isRunbook,
	getRowClassName,
	onSelectionChange,
}: DataTableProps<TData, TValue>) => {
	const isControlledPagination =
		controlledPageIndex !== undefined &&
		controlledPageSize !== undefined &&
		onPaginationChange !== undefined;
	const [rowSelection, setRowSelection] = useState({});
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		{},
	);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [sorting, setSorting] = useState<SortingState>([]);

	const [globalFilter, setGlobalFilter] = useState<string | number>("");

	const table = useReactTable({
		data,
		columns,
		defaultColumn: {
			size: 200,
		},
		filterFns: {
			globalContains: globalContainsFilter,
		},
		state: {
			sorting,
			columnVisibility,
			rowSelection,
			columnFilters,
			globalFilter,
			...(isControlledPagination && {
				pagination: {
					pageSize: controlledPageSize,
					pageIndex: controlledPageIndex,
				},
			}),
		},
		...(isControlledPagination
			? {
					manualPagination: true,
					onPaginationChange,
				}
			: {
					initialState: {
						pagination: {
							pageSize: 50,
							pageIndex: 0,
						},
					},
				}),
		onGlobalFilterChange: setGlobalFilter,
		globalFilterFn: "globalContains",
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: !isControlledPagination
			? getPaginationRowModel()
			: undefined,
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
	});

	useEffect(() => {
		onSelectionChange?.(
			table.getSelectedRowModel().rows.map((r) => r.original),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowSelection]);

	const tableLoadingSkeleton = (
		<TableRow empty>
			<TableCell colSpan={columns.length}>
				<LoadingSkeleton
					rowHeight={8}
					gridGap={2}
					sections={[
						{
							rows: 8,
							columnWidths: ["1"],
						},
					]}
				/>
			</TableCell>
		</TableRow>
	);

	return (
		//pass height as props depending on sibling components
		<div className={cn(height ?? "h-full", "flex flex-col")}>
			{hasToolbar && (
				<Toolbar
					globalFilterValue={
						searchTerm ? searchTerm : (globalFilter ?? "")
					}
					onChange={(globalFilterValue) => {
						if (customSearch) {
							customSearch(String(globalFilterValue));
						} else {
							setGlobalFilter(String(globalFilterValue));
						}
					}}
					table={table}
					color={toolbarColor}
					inputColor={inputColor}
					isRunbook={isRunbook}
					inputPlaceholder={inputPlaceholder}
					filterField={filterField}
				/>
			)}
			{/* scrollable height is container minus heights of toolbar & pagination */}
			<div
				className={cn(
					cn(
						"flex flex-col rounded-lg border border-app-border-strong overflow-clip bg-card-bg",
						isRunbook && "border-app-border",
					),
					hasToolbar
						? isRunbook
							? "h-[calc(100%-3.5rem)] border-t"
							: "h-[calc(100%-3rem)] border-t"
						: "h-full",
				)}
			>
				<ScrollArea
					className={cn(
						showPagination ? "h-[calc(100%-3rem)]" : "h-full",
					)}
				>
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow header key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<TableHead
												key={header.id}
												colSpan={header.colSpan}
												className={headerColor}
												isRunbook={isRunbook}
												style={{
													width: `${header.column.getSize()}px`,
												}}
											>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column
																.columnDef
																.header,
															header.getContext(),
														)}
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						{isLoading ? (
							tableLoadingSkeleton
						) : (
							<TableBody>
								{table.getRowModel().rows?.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow
											key={row.id}
											className={cn(
												(
													row.original as {
														rowStyling?: string;
													}
												)?.rowStyling,
												getRowClassName?.(row.original),
											)}
											data-state={
												row.getIsSelected() &&
												"selected"
											}
										>
											{row
												.getVisibleCells()
												.map((cell) => (
													<TableCell
														key={cell.id}
														className={cn(
															"leading-none",
															expandable &&
																"align-top!",
														)}
													>
														{flexRender(
															cell.column
																.columnDef.cell,
															cell.getContext(),
														)}
													</TableCell>
												))}
										</TableRow>
									))
								) : (
									<TableRow empty>
										<TableCell colSpan={columns.length}>
											<Empty />
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						)}
					</Table>
				</ScrollArea>
				{showPagination && (
					<Pagination
						hideRowsSelected={hideRowsSelected}
						table={table}
						paginationColor={paginationColor}
						isRunbook={isRunbook}
						hasNextPage={
							isControlledPagination
								? hasNextPage
								: !table.getCanNextPage()
						}
					/>
				)}
			</div>
		</div>
	);
};

export { DataTable };
